import { FC, useEffect, useMemo, useState } from "react";
import {
  Table,
  DatePicker,
  Select,
  Input,
  Button,
  notification,
  Modal,
  Tooltip,
} from "antd";
import { DeleteOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
// reuse the Special Days styling so the table / dialog look identical
import style from "../Setting/specialDays/style.module.scss";
import mstyle from "./style.module.scss";
import API from "../../services/api.service";
import { APIS } from "../../constants/apis";
import DayTypeLegend from "../DayTypeLegend";
import {
  villaDayClass,
  villaDayIndex,
  specialDayLabel,
  EMPTY_VILLA_DAYS,
  VillaDayIndex,
} from "../../utils/dayTypes";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Maintenance days management. Moved out of Settings > Special Days into its own
// sidebar section. Uses the existing per-villa maintenance-day endpoints — the
// backend caps them at 5 maintenance days / year / villa.
const Maintenance: FC = () => {
  const [maintList, setMaintList] = useState<any[]>([]);
  const [villas, setVillas] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<
    "latest" | "oldest" | "name" | "name_desc"
  >("latest");

  // add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [villa, setVilla] = useState<number | undefined>(undefined);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");
  // Booked + existing-maintenance days for the villa chosen in the Add dialog,
  // so the date picker can block + highlight them (same as the Bookings picker).
  const [addBooked, setAddBooked] = useState<Set<string>>(new Set());
  const [addMaint, setAddMaint] = useState<Set<string>>(new Set());

  // Peak / high-peak days per picker. Each dialog has its OWN villa, and the
  // API's 14-day decay is villa-specific, so the two can legitimately differ —
  // they can't share one index.
  const [addDays, setAddDays] = useState<VillaDayIndex>(EMPTY_VILLA_DAYS);
  const [editDays, setEditDays] = useState<VillaDayIndex>(EMPTY_VILLA_DAYS);

  // edit dialog (a single maintenance day)
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editVilla, setEditVilla] = useState<number | undefined>(undefined);
  const [editDate, setEditDate] = useState<string>("");
  const [editRemarks, setEditRemarks] = useState<string>("");
  // Booked + existing-maintenance days for the villa in the Edit dialog, so its
  // date picker blocks + highlights them too (the day being edited stays pickable).
  const [editBooked, setEditBooked] = useState<Set<string>>(new Set());
  const [editMaint, setEditMaint] = useState<Set<string>>(new Set());

  const fetchVillas = async () => {
    try {
      const res = await API.post(APIS.HOME.PROPERTY_NAME_LIST, {
        role: "MANAGER",
      });
      if (res.data.status === 1) {
        const vs = res.data.data || [];
        setVillas(vs);
        fetchMaint(vs);
      }
    } catch (e) {
      // silent
    }
  };

  // pull each villa's maintenance days (per-villa endpoint) + tag with villa name
  const fetchMaint = async (villaArr: any[]) => {
    try {
      setLoading(true);
      const results = await Promise.all(
        villaArr.map((v: any) =>
          API.get(APIS.USER_MANAGEMENT.LIST_MAINTENANCE_DAY + v.id)
            .then((r: any) =>
              r.data.status === 1
                ? (r.data.data || []).map((d: any) => ({
                    ...d,
                    villaTitle: v.title,
                  }))
                : []
            )
            .catch(() => [])
        )
      );
      setMaintList(results.flat());
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVillas();
  }, []);

  // Villa-first (Add dialog): when a villa is chosen, pull its calendar so the
  // date-range picker can gray out already-booked + maintenance days (and clear
  // any dates picked for a previous villa). Past days are always blocked.
  useEffect(() => {
    setStartDate("");
    setEndDate("");
    if (!villa) {
      setAddBooked(new Set());
      setAddMaint(new Set());
      setAddDays(EMPTY_VILLA_DAYS);
      return;
    }
    (async () => {
      try {
        const res = await API.get(APIS.BOOKING.CALENDAR + villa);
        const d = res.data?.data || {};
        const toDay = (s: string) => String(s).slice(0, 10); // YYYY-MM-DD
        setAddBooked(new Set<string>((d.booked_date || []).map(toDay)));
        setAddMaint(new Set<string>((d.maintenanceday_dates || []).map(toDay)));
        setAddDays(villaDayIndex(d));
      } catch (e) {
        setAddBooked(new Set());
        setAddMaint(new Set());
        setAddDays(EMPTY_VILLA_DAYS);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [villa]);

  // Edit dialog: load the edit-villa's booked + maintenance days so its date
  // picker can gray them out. Unlike Add, it does NOT clear the chosen date on
  // villa change (the day being edited stays selectable — see editDisabledDate).
  useEffect(() => {
    if (!editVilla) {
      setEditBooked(new Set());
      setEditMaint(new Set());
      setEditDays(EMPTY_VILLA_DAYS);
      return;
    }
    (async () => {
      try {
        const res = await API.get(APIS.BOOKING.CALENDAR + editVilla);
        const d = res.data?.data || {};
        const toDay = (s: string) => String(s).slice(0, 10); // YYYY-MM-DD
        setEditBooked(new Set<string>((d.booked_date || []).map(toDay)));
        setEditMaint(new Set<string>((d.maintenanceday_dates || []).map(toDay)));
        setEditDays(villaDayIndex(d));
      } catch (e) {
        setEditBooked(new Set());
        setEditMaint(new Set());
        setEditDays(EMPTY_VILLA_DAYS);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editVilla]);

  // The backend allows 5 maintenance days per CALENDAR YEAR per villa. The list
  // above already holds every villa's days, so the allowance can be tallied here
  // and surfaced in the villa dropdown instead of only failing on submit.
  const MAX_DAYS_PER_YEAR = 5;

  // villaId:year -> days already booked
  const usedByVillaYear = useMemo(() => {
    const m: { [key: string]: number } = {};
    maintList.forEach((r: any) => {
      const k = `${r.property_id}:${moment(r.date).format("YYYY")}`;
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [maintList]);

  const used = (villaId: number, year: string) =>
    usedByVillaYear[`${villaId}:${year}`] || 0;

  // Dates already under maintenance for a villa — the backend skips these, so
  // they don't count against the allowance here either.
  const blockedFor = (villaId: number) =>
    new Set(
      maintList
        .filter((r: any) => r.property_id === villaId)
        .map((r: any) => moment(r.date).format("YYYY-MM-DD"))
    );

  // Which year(s) the cap is measured against. The dates are picked first, so a
  // range is normally already there (and may straddle a year boundary); the
  // current year is only a placeholder for the villa list before that.
  const addYears = useMemo(() => {
    if (!startDate || !endDate) return [moment().format("YYYY")];
    const ys: string[] = [];
    let cur = moment(startDate).startOf("year");
    const last = moment(endDate).startOf("year");
    while (cur.isSameOrBefore(last, "year")) {
      ys.push(cur.format("YYYY"));
      cur = cur.clone().add(1, "year");
    }
    return ys;
  }, [startDate, endDate]);

  // Days still available across those years — the tightest year decides.
  const remainingFor = (villaId: number, years: string[]) =>
    Math.min(...years.map((y) => MAX_DAYS_PER_YEAR - used(villaId, y)));

  // Days the chosen range would actually add, per year (mirrors the backend:
  // dates already under maintenance are skipped, so they don't consume days).
  const needByYear = (villaId?: number) => {
    const need: { [y: string]: number } = {};
    if (!villaId || !startDate || !endDate) return need;
    const blocked = blockedFor(villaId);
    let cur = moment(startDate);
    const last = moment(endDate);
    while (cur.isSameOrBefore(last, "day")) {
      const ds = cur.format("YYYY-MM-DD");
      if (!blocked.has(ds)) {
        const y = ds.substring(0, 4);
        need[y] = (need[y] || 0) + 1;
      }
      cur = cur.clone().add(1, "day");
    }
    return need;
  };

  // The year this range would push past the cap, if any.
  const overflowYear = (() => {
    if (!villa) return null;
    const need = needByYear(villa);
    for (const y of Object.keys(need)) {
      if (used(villa, y) + need[y] > MAX_DAYS_PER_YEAR) return y;
    }
    return null;
  })();

  // Picking a range in a year the chosen villa has already filled would leave a
  // selection the backend rejects — drop it and say why.
  const addYearsKey = addYears.join(", ");
  useEffect(() => {
    if (!villa || remainingFor(villa, addYears) > 0) return;
    setVilla(undefined);
    notification.warning({
      message: "Villa cleared",
      description: `That villa has already used all ${MAX_DAYS_PER_YEAR} maintenance days in ${addYearsKey}. Please pick another villa.`,
      placement: "top",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addYearsKey]);

  // Same allowance on the edit dialog, measured against the day being edited's
  // own year and ignoring that day itself (moving it around must not count it
  // twice).
  const editYear = editDate
    ? moment(editDate).format("YYYY")
    : moment().format("YYYY");
  const editRemainingFor = (villaId: number) => {
    const self: any = maintList.find((r: any) => r.id === editId);
    const isSelf =
      self &&
      self.property_id === villaId &&
      moment(self.date).format("YYYY") === editYear;
    return MAX_DAYS_PER_YEAR - used(villaId, editYear) + (isSelf ? 1 : 0);
  };

  const resetAdd = () => {
    setVilla(undefined);
    setStartDate("");
    setEndDate("");
    setRemarks("");
  };

  const openAdd = () => {
    resetAdd();
    setAddOpen(true);
  };

  const onAdd = async () => {
    if (!villa || !startDate || !endDate) {
      notification.error({
        message: "Please choose a villa and date",
        placement: "top",
      });
      return;
    }
    try {
      setSaving(true);
      const res = await API.post(APIS.USER_MANAGEMENT.ADD_MAINTENANCE_DAY, {
        property_id: villa,
        start_date: startDate,
        end_date: endDate,
        remarks,
      });
      if (res.data.status === 1) {
        const d = res.data.data || {};
        const blocked = Number(d.blocked ?? 0);
        const skipped = Number(d.skipped ?? 0);
        if (blocked > 0) {
          // something was actually added
          notification.success({
            message:
              skipped > 0
                ? `Added ${blocked} maintenance day(s); ${skipped} were already under maintenance.`
                : `Added ${blocked} maintenance day(s).`,
            placement: "top",
          });
          resetAdd();
          setAddOpen(false);
          fetchMaint(villas);
        } else {
          // nothing added — every selected date is already a maintenance day.
          // Keep the dialog open so the user can pick a different date.
          notification.warning({
            message:
              skipped === 1
                ? "That date is already under maintenance."
                : "Those dates are already under maintenance.",
            placement: "top",
          });
        }
      } else {
        notification.error({
          message: res.data.message || "Failed",
          placement: "top",
        });
      }
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.message || "Something went wrong",
        placement: "top",
      });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row: any) => {
    try {
      const res = await API.delete(
        APIS.USER_MANAGEMENT.DELETE_MAINTENANCE_DAY + row.id
      );
      if (res.data.status === 1) {
        notification.success({
          message: "Maintenance day deleted",
          placement: "top",
        });
        fetchMaint(villas);
      } else {
        notification.error({
          message: res.data.message || "Failed",
          placement: "top",
        });
      }
    } catch (e) {
      notification.error({ message: "Something went wrong", placement: "top" });
    }
  };

  const confirmDelete = (row: any) => {
    Modal.confirm({
      title: "Are you sure you want to delete this maintenance day?",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => onDelete(row),
    });
  };

  const openEdit = (row: any) => {
    setEditId(row.id);
    setEditVilla(row.propertyId);
    setEditDate(moment(row.date).format("YYYY-MM-DD"));
    setEditRemarks(row.remarks || "");
    setEditOpen(true);
  };

  const onUpdate = async () => {
    if (!editId || !editVilla || !editDate) {
      notification.error({
        message: "Villa and date are required",
        placement: "top",
      });
      return;
    }
    try {
      setEditSaving(true);
      const res = await API.put(
        APIS.USER_MANAGEMENT.UPDATE_MAINTENANCE_DAY + editId,
        {
          property_id: editVilla,
          date: editDate,
          remarks: editRemarks,
        }
      );
      if (res.data.status === 1) {
        notification.success({
          message: "Maintenance day updated",
          placement: "top",
        });
        setEditOpen(false);
        fetchMaint(villas);
      } else {
        notification.error({
          message: res.data.message || "Failed",
          placement: "top",
        });
      }
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.message || "Something went wrong",
        placement: "top",
      });
    } finally {
      setEditSaving(false);
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d: string) => moment(d).format("DD MMM YYYY"),
    },
    { title: "Villa", dataIndex: "villaTitle", key: "villa" },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_: any, row: any) => (
        <span className={style.row_actions}>
          <EditOutlined
            className={style.icon}
            onClick={() => openEdit(row)}
          />
          <DeleteOutlined
            className={`${style.icon} ${style.danger}`}
            onClick={() => confirmDelete(row)}
          />
        </span>
      ),
    },
  ];

  const rows = maintList.map((r: any) => ({
    _key: `m-${r.id}`,
    id: r.id,
    name: r.remarks || "Maintenance day",
    remarks: r.remarks || "",
    propertyId: r.property_id,
    villaTitle: r.villaTitle,
    date: r.date,
  }));

  const sorted = [...rows].sort((a: any, b: any) => {
    if (sortOrder === "name") {
      return String(a.name || "").localeCompare(String(b.name || ""));
    }
    if (sortOrder === "name_desc") {
      return String(b.name || "").localeCompare(String(a.name || ""));
    }
    // "Latest" / "Oldest" order by when the maintenance day was CREATED
    // (auto-increment id — higher id == more recently added), not by its date.
    const ra = Number(a.id) || 0;
    const rb = Number(b.id) || 0;
    return sortOrder === "latest" ? rb - ra : ra - rb;
  });

  const q = search.trim().toLowerCase();
  const displayRows = sorted.filter((r: any) => {
    if (!q) return true;
    return (
      String(r.name || "").toLowerCase().includes(q) ||
      String(r.villaTitle || "").toLowerCase().includes(q)
    );
  });

  const datesPicked = !!startDate && !!endDate;
  const addDisabled = !villa || !datesPicked || !!overflowYear;

  // A day is un-pickable if it's in the past, or the chosen villa is already
  // booked / under maintenance that day. Mirrors the Bookings picker.
  const disabledDate = (current: any) => {
    if (!current) return false;
    if (current < moment().startOf("day")) return true;
    const ds = current.format("YYYY-MM-DD");
    return addBooked.has(ds) || addMaint.has(ds);
  };

  // Highlight every day-type the admin needs to see before blocking a date:
  // maintenance and booked (which also block selection), plus peak / high peak,
  // which stay selectable — they are rate markers, not availability. Blocking
  // states win the cell colour so a booked peak day still reads as unavailable.
  const renderDay = (
    current: any,
    isMaint: boolean,
    isBooked: boolean,
    days: VillaDayIndex
  ) => {
    const special = villaDayClass(current, days);
    const cls = isMaint
      ? "cell_maint"
      : isBooked
      ? "cell_booked"
      : special;
    const inner = (
      <div className={`ant-picker-cell-inner ${cls}`}>{current.date()}</div>
    );
    const title = isMaint
      ? "Maintenance day"
      : isBooked
      ? "Booked"
      : specialDayLabel(special);
    return title ? <Tooltip title={title}>{inner}</Tooltip> : inner;
  };

  const dateRender = (current: any) => {
    const ds = current.format("YYYY-MM-DD");
    return renderDay(current, addMaint.has(ds), addBooked.has(ds), addDays);
  };

  // Edit-dialog variants: same blocking/highlighting, but the day currently
  // being edited is always left pickable (it's this record's own maintenance day).
  const editDisabledDate = (current: any) => {
    if (!current) return false;
    const ds = current.format("YYYY-MM-DD");
    if (ds === editDate) return false;
    if (current < moment().startOf("day")) return true;
    return editBooked.has(ds) || editMaint.has(ds);
  };

  const editDateRender = (current: any) => {
    const ds = current.format("YYYY-MM-DD");
    return renderDay(
      current,
      editMaint.has(ds) && ds !== editDate,
      editBooked.has(ds),
      editDays
    );
  };

  const hintStyle: any = { marginTop: 6, fontSize: 12, color: "#8a968f" };
  const warnStyle: any = { marginTop: 6, fontSize: 12, color: "#c0392b" };

  return (
    <div className={`${mstyle.page} ${style.sd_wrap}`}>
      <div className={mstyle.header}>
        <div className={mstyle.heading}>
          <h1 className={mstyle.title}>Maintenance</h1>
          <p className={mstyle.subtitle}>
            <span className={mstyle.count}>{maintList.length}</span> maintenance
            day{maintList.length === 1 ? "" : "s"} across your villas
          </p>
        </div>
        <div className={mstyle.controls}>
          <Input
            allowClear
            placeholder="Search by name or villa"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            prefix={<SearchOutlined style={{ color: "#9aa5a0" }} />}
            style={{ width: 240 }}
          />
          <Select
            value={sortOrder}
            onChange={(v: any) => setSortOrder(v)}
            style={{ width: 170 }}
          >
            <Option value="latest">Sort: Latest</Option>
            <Option value="oldest">Sort: Oldest</Option>
            <Option value="name">Sort: Name (A–Z)</Option>
            <Option value="name_desc">Sort: Name (Z–A)</Option>
          </Select>
          <Button className={style.add_btn} onClick={openAdd}>
            Add Maintenance
          </Button>
        </div>
      </div>

      <div className={style.table_wrap}>
        <Table
          rowKey="_key"
          columns={columns}
          dataSource={displayRows}
          loading={loading}
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      </div>

      {/* add dialog */}
      <Modal
        title={null}
        footer={null}
        visible={addOpen}
        onCancel={() => setAddOpen(false)}
        destroyOnClose
      >
        <div className={style.dialog_title}>Add maintenance day</div>
        <div className={style.field}>
          <label className={style.label}>Villa</label>
          <Select
            style={{ width: "100%" }}
            value={villa}
            placeholder="Select villa"
            onChange={(v) => setVilla(v)}
            showSearch
            optionFilterProp="children"
            listHeight={200}
            getPopupContainer={(trigger: any) => trigger.parentElement}
          >
            {villas.map((p: any) => {
              const left = remainingFor(p.id, addYears);
              // Kept as one plain string so showSearch / optionFilterProp
              // ("children") still matches on the villa name. Villas are only
              // hard-disabled once dates are picked (then addYears is real);
              // before that the dates aren't known, so any villa can be chosen.
              return (
                <Option
                  key={p.id}
                  value={p.id}
                  disabled={datesPicked && left <= 0}
                >
                  {datesPicked && left <= 0
                    ? `${p.title} — all ${MAX_DAYS_PER_YEAR} maintenance days used in ${addYearsKey}`
                    : `${p.title}`}
                </Option>
              );
            })}
          </Select>
          {!villa ? (
            <div style={hintStyle}>
              Pick a villa first — the calendar then blocks its past, booked, and
              maintenance days (booked = dark, maintenance = amber).
            </div>
          ) : datesPicked ? (
            <div style={hintStyle}>
              {remainingFor(villa, addYears)} of {MAX_DAYS_PER_YEAR}{" "}
              maintenance days left for {addYearsKey}.
            </div>
          ) : (
            <div style={hintStyle}>
              Now choose the date range. The {MAX_DAYS_PER_YEAR}-day per-year
              limit is checked once you pick dates.
            </div>
          )}
        </div>

        <div className={style.field} style={{ marginTop: 12 }}>
          <label className={style.label}>Date range</label>
          <DayTypeLegend />
          <RangePicker
            style={{ width: "100%" }}
            dropdownClassName="range_fit_pop"
            disabled={!villa}
            disabledDate={disabledDate}
            dateRender={dateRender}
            value={[
              startDate ? moment(startDate) : null,
              endDate ? moment(endDate) : null,
            ]}
            onChange={(_: any, ds: any) => {
              setStartDate(ds?.[0] || "");
              setEndDate(ds?.[1] || "");
            }}
            format="YYYY-MM-DD"
            getPopupContainer={(trigger: any) => trigger.parentElement}
          />
          {overflowYear && villa ? (
            <div style={warnStyle}>
              This range needs {needByYear(villa)[overflowYear]} day(s) in{" "}
              {overflowYear}, but only{" "}
              {Math.max(0, MAX_DAYS_PER_YEAR - used(villa, overflowYear))} of the{" "}
              {MAX_DAYS_PER_YEAR} are left. Choose a shorter range.
            </div>
          ) : null}
        </div>

        <div className={style.field} style={{ marginTop: 12 }}>
          <label className={style.label}>Remarks</label>
          <Input
            placeholder="e.g. renovation"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </div>

        <div className={style.dialog_footer}>
          <Button
            className={style.dialog_cancel_btn}
            onClick={() => setAddOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className={style.add_btn}
            loading={saving}
            disabled={addDisabled}
            onClick={onAdd}
          >
            Add
          </Button>
        </div>
      </Modal>

      {/* edit dialog — a single maintenance day */}
      <Modal
        title={null}
        footer={null}
        visible={editOpen}
        onCancel={() => setEditOpen(false)}
        destroyOnClose
      >
        <div className={style.dialog_title}>Edit maintenance day</div>
        <div className={style.field}>
          <label className={style.label}>Villa</label>
          <Select
            style={{ width: "100%" }}
            value={editVilla}
            placeholder="Select villa"
            onChange={(v) => setEditVilla(v)}
            showSearch
            optionFilterProp="children"
            listHeight={200}
            getPopupContainer={(trigger: any) => trigger.parentElement}
          >
            {villas.map((p: any) => {
              const left = editRemainingFor(p.id);
              return (
                <Option key={p.id} value={p.id} disabled={left <= 0}>
                  {left <= 0
                    ? `${p.title} — all ${MAX_DAYS_PER_YEAR} maintenance days used in ${editYear}`
                    : `${p.title} — ${left} of ${MAX_DAYS_PER_YEAR} days left in ${editYear}`}
                </Option>
              );
            })}
          </Select>
        </div>

        <div className={style.field} style={{ marginTop: 12 }}>
          <label className={style.label}>Date</label>
          <DayTypeLegend />
          <DatePicker
            style={{ width: "100%" }}
            value={editDate ? moment(editDate) : null}
            disabledDate={editDisabledDate}
            dateRender={editDateRender}
            onChange={(_, ds) => setEditDate(ds || "")}
            format="YYYY-MM-DD"
            getPopupContainer={(trigger: any) => trigger.parentElement}
          />
        </div>

        <div className={style.field} style={{ marginTop: 12 }}>
          <label className={style.label}>Remarks</label>
          <Input
            placeholder="e.g. renovation"
            value={editRemarks}
            onChange={(e) => setEditRemarks(e.target.value)}
          />
        </div>

        <div className={style.dialog_footer}>
          <Button
            className={style.dialog_cancel_btn}
            onClick={() => setEditOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className={style.add_btn}
            loading={editSaving}
            disabled={!editVilla || !editDate}
            onClick={onUpdate}
          >
            Update
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Maintenance;
